import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { authService, extractApiError } from '../api/services';
import './Auth.css';
import logo from '../assets/rush-republic-logo.png';

/** Mirrors ForgotPassword.js's own list -- same rules the backend's enforceStrongPassword checks. */
const PASSWORD_RULES = [
  { test: (v) => v.length >= 8, label: 'At least 8 characters' },
  { test: (v) => /[A-Z]/.test(v), label: 'One uppercase letter' },
  { test: (v) => /[a-z]/.test(v), label: 'One lowercase letter' },
  { test: (v) => /\d/.test(v), label: 'One number' },
  { test: (v) => /[!@#$%^&*(),.?":{}|<>_\-[\];'\\/`~+=]/.test(v), label: 'One special character' },
];

/**
 * First-time login password setup -- reached only via Login.js navigating
 * here with {email, setupToken} in router state, after the backend confirms
 * the account's initial (Admin-set) password is correct but must_change_password
 * is still true. There is no "Back to Login" link here on purpose: the user
 * must not be able to bypass the required setup and land back on a screen
 * that just re-triggers this same redirect.
 */
export default function SetNewPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, setupToken } = location.state || {};

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reached directly (refresh, bookmark, back button) with no token in
  // state -- there is nothing to authorize this screen with, so send the
  // user back to Login rather than rendering a form that can't submit.
  if (!email || !setupToken) {
    return <Navigate to="/login" replace />;
  }

  const failedRules = PASSWORD_RULES.filter((rule) => !rule.test(password));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password && !confirmPassword) {
      setError('Enter a new password.');
      return;
    }
    if (!password) {
      setError('Enter a new password.');
      return;
    }
    if (!confirmPassword) {
      setError('Re-enter the new password.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (failedRules.length > 0) {
      setError(`Password must include: ${failedRules.map((r) => r.label.toLowerCase()).join(', ')}.`);
      return;
    }

    setSubmitting(true);
    try {
      await authService.firstTimePasswordSetup(email, setupToken, password, confirmPassword);
      navigate('/login', { replace: true, state: { passwordSetupDone: true } });
    } catch (err) {
      setError(extractApiError(err, 'Could not set your password. Please try again.'));
      setSubmitting(false);
    }
  };

  return (
    <div className="rr-auth">
      <div className="rr-auth__panel">
        <div className="rr-auth__brand">
          <img src={logo} alt="Rush Republic" className="rr-auth__logo" />
          <h1 className="rr-auth__company">Rush Republic</h1>
          <p className="rr-auth__tagline">Employee Management Portal</p>
        </div>
        <hr className="rr-auth__divider" />
        <h2 className="rr-auth__title">Set New Password</h2>

        {error && <div className="rr-form-error">{error}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="rr-field">
            <label htmlFor="password">Enter New Password</label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="rr-field">
            <label htmlFor="confirmPassword">Re-Enter New Password</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <button type="submit" className="rr-submit" disabled={submitting}>
            {submitting ? 'Resetting…' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
